// Treatment-related thunks
import moment from 'moment';
import { dispatch, store } from '../../store';
import { updateError } from '../user';
import { callApi } from '../../../utils/apiCall';
import { isSeparatelyRequestedFutureTreatmentBooking, captureTreatmentAction } from '../../../utils/attendanceActions.ts';
import { invalidateAttendance } from '../attendanceReview.ts';
import { errorHandler } from '../../../utils/errorHandler';
import { getCurrentTreatmentIdV2, getPracticeId } from '../clincheck';
import { setLoader } from '../loader';
import { leadActions, startLoadingData, updateCompleteStatus, startLoadingDepositPending } from './slice';

import { followUpSlice, addAppointment, UpdateAppointment } from './appointmentThunks';

// Shared by atomic lead creation and treatment-only additions. `data.id` is the
// created LeadTreatment ID, not the nested PracticeTreatment ID.
export function continueCreatedTreatment(data, detail, practiceId, id, handleClose, teethData, isAddTreatmentWhileBooking) {
    return async () => {
        const { lead, newLeadDetail } = store.getState().lead;
        const results = (lead?.results || []).map((item) => (
            item?.id === data?.lead ? { ...item, lead_treatments: [data] } : item
        ));
        let closed = false;
        const close = (status, leadId) => {
            if (closed) return;
            closed = true;
            handleClose?.(status, leadId, data.id);
        };
        dispatch(leadActions.updateLead({ results, count: lead?.count }));
        dispatch(leadActions.saveTreatment(data));
        if (isAddTreatmentWhileBooking) {
            dispatch(leadActions.updateLeadTreatment([data]));
            dispatch(updateError({ message: 'Treatment Added Successfully', success: true }));
            setTimeout(() => handleClose?.(), 500);
            return;
        }
        const bookAppointment = () => dispatch(addAppointment({
            ...newLeadDetail.lead_appointment,
            lead_treatment: data.id,
        }, practiceId, id, close, 'CreatLead'));
        if (newLeadDetail?.addAppointment && newLeadDetail?.attentendAppoiment?.activeTab === 'Starting treatment') {
            await bookAppointment();
            return;
        }
        if (teethData?.needToUpdate) {
            // Finish the specification before booking so a failed PATCH cannot
            // race a successful appointment into closing/saving the call script.
            await dispatch(editTreatment({
                ...detail,
                lead: id,
                id: data.id,
                leadtreatment_specification: { lead_treatment: data.id, teeth_count: teethData.teeth },
            }, newLeadDetail?.addAppointment ? bookAppointment : close, 'editTreatment'));
        } else if (newLeadDetail?.addAppointment) {
            await bookAppointment();
        } else {
            close('finish', id);
            dispatch(startLoadingData(false));
            dispatch(updateError({ message: 'Treatment added successfully', success: true }));
        }
    };
}

export function addTreatment(detail, practiceId, id, handleClose, teethData, isAddTreatmentWhileBooking) {
    return async () => {
        try {
            let response;
            if (detail?.treatment)
                response = await callApi(`/api/v1/practices/${practiceId}/leads/${id}/treatments/`, 'post', detail);
            else {
                dispatch(updateCompleteStatus(false));
                dispatch(startLoadingData(false));
                handleClose();
                return;
            }
            if (response?.status === 201) {
                await dispatch(continueCreatedTreatment(response.data, detail, practiceId, id, handleClose, teethData, isAddTreatmentWhileBooking));
                return;
            }
            dispatch(updateCompleteStatus(false));
            dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
            dispatch(startLoadingData(false));
        } catch (error) {
            dispatch(updateCompleteStatus(false));
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false, cause: error }));
            dispatch(startLoadingData(false));
        }
    };
}

export function UpdateTreatmentV2(data, practiceId, handleClose, status, options = {}) {
    return async () => {
        try {
            let detail = {
                ...data
            };
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${data?.lead}/treatments/${data.id}/`);
            if (response?.status === 200) {
                if (response?.data?.leadtreatment_specification?.id) {
                    detail = {
                        ...detail,
                        leadtreatment_specification: {
                            teeth_count: detail?.teethAmount || 0
                        }
                    };
                } else {
                    detail = {
                        ...detail,
                        leadtreatment_specification: {
                            lead_treatment: data.id,
                            teeth_count: detail?.teethAmount || 0
                        }
                    };
                }
                await dispatch(editTreatment(detail, handleClose, status, options));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message), success: false, cause: error }));
        }
    };
}
export function UpdateTreatmentV3(data, type, handleSuccess) {
    return async () => {
        try {
            let detail = {
                ...data.detail
            };
            const practiceId = getPracticeId();
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${data?.lead_id}/treatments/${data.treatment_id}/`);
            if (response?.status === 200) {
                if (response?.data?.leadtreatment_specification?.id) {
                    detail = {
                        ...detail,
                        leadtreatment_specification: {
                            teeth_count: detail?.teethAmount || 0
                        }
                    };
                } else {
                    detail = {
                        ...detail,
                        leadtreatment_specification: {
                            lead_treatment: data.treatment_id,
                            teeth_count: detail?.teethAmount || 0
                        }
                    };
                }
                dispatch(updateTreatmentDetail(practiceId, data?.lead_id, data?.treatment_id, detail, handleSuccess, type));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message), success: false, cause: error }));
        }
    };
}
export function UpdateTreatment(data, practiceId, handleClose, status) {
    return async () => {
        try {
            const capturePracticeId = practiceId;
            const paymentEditOnly = store.getState().practice?.practice?.preferences?.is_payment_enforced_before_treatment && data.depositStatus;
            if (!['RescheduleTreatment', 'updateLeadDetail', 'editTreatment'].includes(status) && !paymentEditOnly) {
                const updateData = { ...data }; delete updateData.lead; delete updateData.id;
                await dispatch(StartingTreatment(data, capturePracticeId, handleClose, status, { preparation: {
                    url: `/api/v1/practices/${capturePracticeId}/leads/${data?.lead}/treatments/${data.id}/`, method: 'put', data: updateData,
                } }));
                return;
            }
            const detail = {
                ...data
            };
            delete detail.lead;
            delete detail.id;

            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${data?.lead}/treatments/${data.id}/`, 'put', detail);
            if (response?.status === 200) {
                const { newLeadDetail: { startTreatment, lead_appointment: leadAppointments }, createLead, tableRowLead, followLead } = store.getState().lead;
                if (status === 'RescheduleTreatment') {
                    if (tableRowLead?.status === 'FAILED_TO_ATTEND_CONSULTATION' || tableRowLead?.status === 'FAILED_TO_ATTEND') {
                        dispatch(addAppointment(startTreatment?.appointmentDetail, practiceId, createLead.id, handleClose, 'Reschedule'));
                        return;
                    }
                    dispatch(UpdateAppointment(startTreatment?.appointmentDetail, practiceId, handleClose, 'Reschedule'));
                    return;
                }
                if (status === 'updateLeadDetail') {
                    const leadId = createLead.lead_treatment?.lead || followLead?.id || createLead.id;
                    const appointmentDetail = {
                        ...leadAppointments,
                        lead_treatment: createLead?.lead_treatment?.id,
                    };
                    dispatch(addAppointment(appointmentDetail, practiceId, leadId, handleClose, 'followUp'));
                    return;
                }
                if (status === 'editTreatment') {
                    dispatch(updateError({ success: true, message: 'Treatment updated successfully' }));
                    dispatch(startLoadingData(false));
                    handleClose('finish', data?.lead);
                    return;
                }
                const { practice } = store.getState().practice;
                const isPaymentMandatory = practice?.preferences?.is_payment_enforced_before_treatment;
                if (isPaymentMandatory && data.depositStatus) return;
                const startTreatments = {
                    ...startTreatment.updatedTreatment,
                    lead: data?.lead,
                    id: data.id
                };
                dispatch(StartingTreatment(startTreatments, practiceId, handleClose, status));
                return;
            }
            dispatch(updateCompleteStatus(false));
            dispatch(updateError({ message: JSON.stringify(response.data || response.detail), success: false }));
            dispatch(startLoadingData(false));
            document.body.classList.remove('points-event');
        } catch (error) {
            dispatch(updateCompleteStatus(false));
            dispatch(updateError({ message: JSON.stringify(error.message), success: false, cause: error }));
            dispatch(startLoadingData(false));
        }
    };
}

export function editTreatment(data, handleClose, status, options = {}) {
    return async () => {
        try {
            const capturePracticeId = options.bookingCompletion?.practiceId ?? getPracticeId();
            const paymentEditOnly = store.getState().practice?.practice?.preferences?.is_payment_enforced_before_treatment && data.depositStatus;
            if (!['RescheduleTreatment', 'updateLeadDetail', 'editTreatment'].includes(status) && !paymentEditOnly) {
                const updateData = { ...data }; delete updateData.lead; delete updateData.id;
                await dispatch(StartingTreatment(data, capturePracticeId, handleClose, status, { ...options, preparation: {
                    url: `/api/v1/practices/${capturePracticeId}/leads/${data?.lead}/treatments/${data.id}/`, method: 'PATCH', data: updateData,
                } }));
                return;
            }
            const detail = {
                ...data
            };
            delete detail.lead;
            delete detail.id;
            const practiceId = capturePracticeId;
            if (options.bookingCompletion && !options.bookingCompletion.isScopeCurrent()) return;
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${data?.lead}/treatments/${data.id}/`, 'PATCH', detail);
            if (response?.status === 200) {
                if (options.bookingCompletion) {
                    const context = options.bookingCompletion;
                    if (!context.isScopeCurrent()) return;
                    await dispatch(addAppointment(context.appointment, context.practiceId, context.leadId, handleClose, 'followUp', undefined, context.isScopeCurrent, context));
                    return;
                }
                const { newLeadDetail: { startTreatment, lead_appointment: leadAppointments }, createLead, tableRowLead, followLead } = store.getState().lead;
                if (status === 'RescheduleTreatment') {
                    if (tableRowLead?.status === 'FAILED_TO_ATTEND_CONSULTATION' || tableRowLead?.status === 'FAILED_TO_ATTEND') {
                        dispatch(addAppointment(startTreatment?.appointmentDetail, practiceId, createLead.id, handleClose, 'Reschedule'));
                        return;
                    }
                    dispatch(UpdateAppointment(startTreatment?.appointmentDetail, practiceId, handleClose, 'Reschedule'));
                    return;
                }
                if (status === 'updateLeadDetail') {
                    const leadId = createLead.lead_treatment?.lead || followLead?.id || createLead.id;
                    const appointmentDetail = {
                        ...leadAppointments,
                        lead_treatment: createLead?.lead_treatment?.id,
                    };
                    dispatch(addAppointment(appointmentDetail, practiceId, leadId, handleClose, 'followUp'));
                    return;
                }
                if (status === 'editTreatment') {
                    dispatch(updateError({ success: true, message: 'Treatment updated successfully' }));
                    dispatch(startLoadingData(false));
                    handleClose('finish', data?.lead);
                    return;
                }
                const { practice } = store.getState().practice;
                const isPaymentMandatory = practice?.preferences?.is_payment_enforced_before_treatment;
                if (isPaymentMandatory && data.depositStatus) return;
                const startTreatments = {
                    ...startTreatment.updatedTreatment,
                    lead: data?.lead,
                    id: data.id
                };
                dispatch(StartingTreatment(startTreatments, practiceId, handleClose, status));
                return;
            }
            dispatch(updateCompleteStatus(false));
            dispatch(updateError({ message: JSON.stringify(response.data || response.detail), success: false }));
            dispatch(startLoadingData(false));
            dispatch(setLoader(false));
            document.body.classList.remove('points-event');
        } catch (error) {
            dispatch(updateCompleteStatus(false));
            dispatch(updateError({ message: JSON.stringify(error.message), success: false, cause: error }));
            dispatch(startLoadingData(false));
            dispatch(setLoader(false));
        }
    };
}

export function CompleteTreatment(data, leadId, id, practiceId, handleClose) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${id}/treatment_closed/`, 'post', data);
            if (response?.status === 200) {
                dispatch(updateError({ success: true, message: 'Treatment completed successfully' }));
                dispatch(updateCompleteStatus(false));
                handleClose('finish', leadId);
                dispatch(startLoadingData(false));
                return;
            }
            dispatch(setLoader(false));
            dispatch(updateCompleteStatus(false));
            dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
            dispatch(startLoadingData(false));
        } catch (error) {
            dispatch(setLoader(false));
            dispatch(updateCompleteStatus(false));
            errorHandler(error, '', updateError);
            dispatch(startLoadingData(false));
        }
    };
}

// Module-level in-flight guard for treatment_started/ POSTs. Keyed by the
// LeadTreatment id being started. A second dispatch for the SAME treatment
// while the first is still in flight is dropped rather than re-POSTed —
// production saw 4 POSTs to treatment_started/ in 12s from a re-enabled
// Yes/Finish button (Sentry JAVASCRIPT-REACT-1BY/1BZ/1C0/1C1/1C2, incident
// 2026-10-05). Cleared in `finally` so it can never strand a treatment.
const inFlightTreatmentStarts = new Set();

// The Start treatment form's "Appointment notes" are posted with the NEW appointment it
// schedules (addAppointment → addAppointmentNotes). With no appointment date the form
// still accepts notes but nothing ever saved them (30 Oct 2026 flag-on smoke, staging
// lead 23373: typed note gone, no warning). Attach them to the consultation the start
// just confirmed instead — the receipt's appointment on capture practices, else the
// treatment's newest ATTENDED consultation — and say so when there is none.
const legacyApiMoment = (value) => moment(value, 'DD-MM-YYYY HH:mm:ss');
export const attendedConsultationFor = (treatment, appointmentId) => {
    const rows = Array.isArray(treatment?.appointments) ? treatment.appointments : [];
    if (appointmentId) return rows.find((row) => String(row?.id) === String(appointmentId)) || { id: appointmentId };
    return rows
        .filter((row) => row?.status === 'ATTENDED' && (row?.type ?? 'CONSULTATION') === 'CONSULTATION')
        .sort((a, b) => legacyApiMoment(b.datetime).valueOf() - legacyApiMoment(a.datetime).valueOf())[0] || null;
};
export async function preserveStartTreatmentNote({ practiceId, leadId, note, appointmentId, treatment }) {
    const text = String(note ?? '').trim();
    if (!text) return;
    const target = attendedConsultationFor(treatment, appointmentId);
    if (!target?.id) {
        dispatch(updateError({ success: false, message: 'Treatment started, but there is no attended consultation to attach your appointment notes to. Add them as a lead note.' }));
        return;
    }
    const saved = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/appointments/${target.id}/appointment_notes/`, 'post', { additional_notes: text });
    if (saved?.status === 201) {
        dispatch(updateError({ success: true, message: 'Appointment notes saved to the attended consultation' }));
        return;
    }
    dispatch(updateError({ success: false, message: 'Treatment started, but your appointment notes could not be saved. Add them as a lead note.' }));
}

export function StartingTreatment(data, practiceId, handleClose, status, options = {}) {
    return async () => {
        const treatmentKey = getCurrentTreatmentIdV2(data.id);
        if (treatmentKey != null && inFlightTreatmentStarts.has(treatmentKey)) {
            return;
        }
        if (treatmentKey != null) inFlightTreatmentStarts.add(treatmentKey);
        try {
            const treatmentDetail = {
                ...data,
            };
            delete treatmentDetail.lead;
            delete treatmentDetail.id;
            const outcome = await captureTreatmentAction({ practiceId, leadId: data?.lead, treatmentId: treatmentKey, targetStatus: 'TREATMENT_STARTED', extraData: treatmentDetail, preparation: options.preparation, attendanceReceipt: options.attendanceReceipt });
            if (outcome.status !== 'success') {
                dispatch(setLoader(false)); dispatch(updateCompleteStatus(false)); dispatch(startLoadingData(false));
                options.onCancel?.();
                return;
            }
            const response = outcome.data;
            dispatch(invalidateAttendance({ practiceId, leadId: data?.lead, treatmentId: treatmentKey }));
            if (response?.status === 200) {
                // Legacy practices get a receipt derived from the treatment (see
                // legacyReceiptFromTreatment); capture practices get the real one.
                options.onCaptured?.(outcome.receipt ?? response.data);
                // Backend treats a repeat call on an already-started treatment as a
                // success (200 with the current treatment, status TREATMENT_STARTED)
                // rather than a 500 — both a fresh start and a no-op repeat land here.
                if (status === 'startingTreatmentFinish') {
                    dispatch(updateError({ success: true, message: 'Treatment started successfully' }));
                    dispatch(updateCompleteStatus(false));
                    await handleClose('finish', treatmentKey);
                    dispatch(startLoadingData(false));
                    return;
                }
                const {
                    newLeadDetail: {
                        startTreatment,
                    },
                    createLead
                } = store.getState().lead;
                const appointmentDetail = {
                    ...startTreatment.appointmentDetail,
                    lead_treatment: createLead?.lead_treatment?.id,
                };
                if (isSeparatelyRequestedFutureTreatmentBooking(appointmentDetail)) {
                    // The treatment has already started successfully at this point —
                    // report that now, independently of the follow-on treatment
                    // appointment below. If that appointment POST fails, the user
                    // must never read it as "treatment didn't start" (it did), and
                    // must never be invited to press Finish again, which would
                    // re-POST treatment_started/ for an already-started treatment.
                    dispatch(updateError({ success: true, message: 'Treatment started successfully' }));
                    dispatch(updateCompleteStatus(false));
                    await dispatch(addAppointment(appointmentDetail, practiceId, data?.lead, handleClose, 'treatmentStartedAppointment'));
                    return;
                }
                dispatch(updateError({ success: true, message: 'Treatment started successfully' }));
                dispatch(updateCompleteStatus(false));
                await preserveStartTreatmentNote({ practiceId, leadId: data?.lead, note: appointmentDetail?.note,
                    appointmentId: outcome.receipt?.appointment_id, treatment: response.data });
                await handleClose('finish', data?.lead);
                dispatch(startLoadingData(false));
                return;
            }
            if (response?.status === 409 && response?.data?.code === 'invalid_treatment_transition') {
                // Backend FSM rejected the move (the treatment is no longer in a
                // status this action can start from) — tell the user why instead of
                // the generic "Something went wrong!". Deliberately NOT calling
                // handleClose: callers' close handlers complete the actioned task
                // (NewLead handleClose reads markAsComplete from its last render),
                // and a failed start must never complete a task. Repeat clicks are
                // harmless now (in-flight guard + backend 200 on repeat).
                dispatch(setLoader(false));
                dispatch(updateCompleteStatus(false));
                dispatch(updateError({
                    message: response.data.detail || "This treatment can't be started from its current status",
                    success: false,
                }));
                dispatch(startLoadingData(false));
                return;
            }
            dispatch(setLoader(false));
            dispatch(updateCompleteStatus(false));
            dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
            dispatch(leadActions.hasError(response.data || 'Something went wrong'));
            // No handleClose on failure — see the 409 branch above.
            dispatch(startLoadingData(false));
        } catch (error) {
            options.onCancel?.();
            dispatch(setLoader(false));
            dispatch(updateCompleteStatus(false));
            dispatch(updateError({ message: JSON.stringify(error.message), success: false, cause: error }));
            dispatch(leadActions.hasError(error.message));
            // No handleClose on failure — see the 409 branch above.
            dispatch(startLoadingData(false));
        } finally {
            if (treatmentKey != null) inFlightTreatmentStarts.delete(treatmentKey);
        }
    };
}

export function updateTreatmentDetail(practiceId, leadId, id, detail, handleSuccess, type, handleReloadAPI) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${id}/`, 'patch', detail);
            if (response?.status === 200) {
                if (handleReloadAPI) {
                    handleReloadAPI('finish');
                }
                if (type === 'leadDataGrid') {
                    dispatch(updateError({ success: true, message: 'Treatment updated successfully' }));
                    return;
                }
                if (type === 'deposit_cell') {
                    handleSuccess({ success: true, message: 'Deposit status updated successfully', updateTreatment: true, event: type });
                    return;
                }
                if (type === 'choice_cell') {
                    handleSuccess({ success: true, message: 'Lead updated successfully', updateTreatment: true, event: type });
                    return;
                }
                handleSuccess({ success: true, message: 'Treatment updated successfully', updateTreatment: true, event: type });
            } else {
                dispatch(updateError({ message: JSON.stringify(response?.data) || 'Something went wrong', success: false }));
                dispatch(setLoader(false));
            }
        } catch (error) {
            handleSuccess({ success: false, message: error.message || 'Something went wrong' });
            dispatch(leadActions.hasError(error.message));
            dispatch(setLoader(false));
        }
    };
}

export function deleteTreatment(practiceId, leadId, id, handleNext) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${id}/`, 'delete');
            if (response?.status === 204) {
                handleNext?.({ success: true, message: 'Treatment cancelled', cancelTreatment: true });
                dispatch(followUpSlice(leadId, practiceId, 'LeadDetail'));
            } else {
                // A coded refusal from the API (e.g. last_treatment_cannot_be_removed,
                // 409) carries a sentence written for staff in `detail`. Show that
                // verbatim — the Object.entries fallback below is for field-level
                // validation errors and turns a good message into
                // "code: ..., detail: ..., lead_treatment_id: ...".
                const message = response?.data?.detail
                    || (response?.data && typeof response.data === 'object'
                        ? Object.entries(response.data).map(([k, v]) => `${k}: ${v}`).join(', ')
                        : `Failed to delete treatment (HTTP ${response?.status || 'timeout'})`);
                handleNext?.({ message, success: false });
                dispatch(leadActions.hasError(message));
            }
        } catch (error) {
            handleNext?.({ success: false, message: error.message || 'Something went wrong' });
            dispatch(leadActions.hasError(error.message));
        }
    };
}

export function getIndividualTreatment(data, handleClose) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${getPracticeId()}/leads/${data?.leadId}/treatments/${data.treatmentId}/`);
            dispatch(leadActions.setTreatmentLoading(false));
            if (response?.status === 200) {
                dispatch(leadActions.setCurrentTreatmentData(response?.data));
                if (handleClose) {
                    handleClose();
                }
                return;
            }
            dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
        } catch (error) {
            dispatch(leadActions.setTreatmentLoading(false));
            dispatch(updateError({ message: JSON.stringify(error.message), success: false, cause: error }));
        }
    };
}

export function UpdateDepositPendingStatus(data, handleClose) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${getPracticeId()}/leads/${data?.leadId}/treatments/${data.treatmentId}/`, 'put', data?.payload);
            if (response?.status === 200) {
                dispatch(leadActions.setCurrentTreatmentData(response?.data));
                if (handleClose) {
                    handleClose();
                } else {
                    dispatch(leadActions.setTreatmentLoading(false));
                }
                dispatch(updateError({ message: JSON.stringify('Deposit status updated successfully'), success: true }));
                return;
            }
            dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
            dispatch(startLoadingDepositPending(false));
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message), success: false, cause: error }));
            dispatch(startLoadingDepositPending(false));
        }
    };
}

export function consultationAttended(practiceId, leadId, id, handleSuccess, type, options = {}) {
    return async () => {
        try {
            const outcome = await captureTreatmentAction({ practiceId, leadId, treatmentId: id, targetStatus: 'CONSULTATION_ATTENDED' });
            if (outcome.status !== 'success') {
                dispatch(setLoader(false)); dispatch(updateCompleteStatus(false)); dispatch(startLoadingData(false));
                options.onCancel?.();
                return;
            }
            const response = outcome.data;
            dispatch(invalidateAttendance({ practiceId, leadId, treatmentId: id }));
            if (response?.status === 200) {
                if (type === 'TreatmentAccordion') {
                    handleSuccess({ success: true });
                    dispatch(startLoadingData(false));
                    return;
                }
                handleSuccess({ success: true, message: 'Lead status updated successfully' });
            } else {
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false, cause: error }));
            dispatch(updateCompleteStatus(false));
            options.onCancel?.();
        }
    };
}

export function revertToFailedToAttend(practiceId, leadId, id, handleSuccess) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${id}/revert_failed_to_attend/`, 'post');
            if (response?.status === 200) {
                handleSuccess({ success: true });
                dispatch(startLoadingData(false));
                return;
            }
            if (response?.status === 409) {
                const detail = response?.data?.detail || 'Treatment is not in the correct status to revert';
                dispatch(updateError({ message: detail, success: false }));
                return;
            }
            dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false, cause: error }));
        }
    };
}

// "Back to Attended" on a started treatment. Dedicated backend correction
// (revert_consultation_attended/); consultation_attended/ deliberately no-ops on a
// started treatment (preserve_advanced), which made this menu item do nothing.
export function revertToConsultationAttended(practiceId, leadId, id, handleSuccess) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${id}/revert_consultation_attended/`, 'post');
            if (response?.status === 200) {
                dispatch(invalidateAttendance({ practiceId, leadId, treatmentId: id }));
                handleSuccess({ success: true });
                dispatch(startLoadingData(false));
                return;
            }
            if (response?.status === 409) {
                dispatch(updateError({ message: response?.data?.detail || 'Treatment is not in the correct status to revert', success: false }));
                return;
            }
            if (response?.status === 404) {
                dispatch(updateError({ message: 'Back to Attended is not available on this server yet.', success: false }));
                return;
            }
            dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false, cause: error }));
        }
    };
}

export function revertToConsultationBooked(practiceId, leadId, id, handleSuccess) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${id}/revert_consultation_booked/`, 'post');
            if (response?.status === 200) {
                handleSuccess({ success: true });
                dispatch(startLoadingData(false));
                return;
            }
            if (response?.status === 409) {
                const detail = response?.data?.detail || 'Treatment is not in the correct status to revert';
                dispatch(updateError({ message: detail, success: false }));
                return;
            }
            dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false, cause: error }));
        }
    };
}

export function alignerApproveStatusMail(data, handleSuccess) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/leads-aligner-approve/update-status/`, 'post', data);
            if (response?.status === 200) {
                handleSuccess({ success: true, message: 'Aligner approve status updated successfully' });
            } else {
                handleSuccess({ success: false, message: JSON.stringify(response?.data) || 'Something went wrong' });
            }
        } catch (error) {
            handleSuccess({ success: false, message: error.message || 'Something went wrong' });
        }
    };
}
